/**
 * Two-Factor Authentication (2FA) API Service Module
 *
 * Handles 2FA setup, verification, and management.
 * All endpoints require Bearer token authentication (automatically injected).
 *
 * @module api/2fa
 * @example
 * ```typescript
 * import { twoFaAPI } from '@/api';
 *
 * // Check 2FA status
 * const status = await twoFaAPI.getStatus();
 *
 * // Enable 2FA
 * const result = await twoFaAPI.enable();
 *
 * // Verify with code
 * await twoFaAPI.verify({ code: '123456' });
 * ```
 */

import { api } from './client';
import type {
  TwoFactorStatus,
  TwoFactorVerifyRequest,
} from './types';

/**
 * Two-Factor Authentication API endpoints
 */
export const twoFaAPI = {
  /**
   * GET /api/2fa/status
   *
   * Check the current 2FA status for the user's account.
   *
   * @returns 2FA status information
   *
   * @example
   * ```typescript
   * const status = await twoFaAPI.getStatus();
   *
   * // Returns:
   * // {
   * //   enabled: true,
   * //   verified: true
   * // }
   * ```
   *
   * @required_headers
   * - Authorization: "Bearer <access_token>" (automatically injected)
   *
   * @throws ApiError if token is invalid or expired
   */
  getStatus: async (): Promise<TwoFactorStatus> =>
    api.get<TwoFactorStatus>('/api/2fa/status'),

  /**
   * POST /api/2fa/enable
   *
   * Initiate 2FA setup. Returns a QR code or secret key for authenticator app setup.
   * User must verify with a code from their authenticator app to complete setup.
   *
   * @example
   * ```typescript
   * const setup = await twoFaAPI.enable();
   *
   * // Returns QR code or secret for setting up authenticator app
   * // Display to user and ask them to scan/enter in their authenticator
   * ```
   *
   * @required_headers
   * - Authorization: "Bearer <access_token>" (automatically injected)
   *
   * @throws ApiError if token is invalid or expired
   */
  enable: async (): Promise<any> =>
    api.post('/api/2fa/enable', {}),

  /**
   * POST /api/2fa/verify
   *
   * Verify 2FA code from authenticator app.
   * Must be called after enable() to complete 2FA setup.
   *
   * @param request - Verification request
   * @param request.code - 6-digit code from authenticator app
   *
   * @example
   * ```typescript
   * await twoFaAPI.verify({ code: '123456' });
   *
   * // Returns confirmation that 2FA is now enabled
   * ```
   *
   * @required_headers
   * - Authorization: "Bearer <access_token>" (automatically injected)
   * - Content-Type: "application/json" (automatically set)
   *
   * @throws ApiError if:
   * - Token is invalid or expired
   * - Code is invalid or expired
   * - 2FA is not being set up
   */
  verify: async (request: TwoFactorVerifyRequest): Promise<any> =>
    api.post('/api/2fa/verify', request),

  /**
   * POST /api/2fa/disable
   *
   * Disable 2FA for the user's account.
   * Requires verification with current 2FA code.
   *
   * @param request - Disable request with verification code
   * @param request.code - 6-digit code from authenticator app
   *
   * @example
   * ```typescript
   * await twoFaAPI.disable({ code: '123456' });
   *
   * // Returns confirmation that 2FA is now disabled
   * ```
   *
   * @required_headers
   * - Authorization: "Bearer <access_token>" (automatically injected)
   * - Content-Type: "application/json" (automatically set)
   *
   * @throws ApiError if:
   * - Token is invalid or expired
   * - Code is invalid or expired
   * - 2FA is not currently enabled
   */
  disable: async (request: TwoFactorVerifyRequest): Promise<any> =>
    api.post('/api/2fa/disable', request),
};
